import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Columns, Square, Maximize2, ExternalLink, Check, ChevronDown, X,
} from '../../icons';
import { useModalLeave } from '../../hooks/useModalLeave';
import { PaneResizer } from '../shared/PaneResizer';
import { AiConversation } from './AiConversation';
import { AiComposer } from './AiComposer';
import {
  useAiPanelStore, AI_PANEL_MIN_WIDTH, AI_PANEL_MAX_WIDTH, AI_PANEL_DEFAULT_WIDTH,
  type AiDock,
} from '../../store/aiPanelStore';
import { AI_HOME_PATH } from '../../lib/ai-menu';
import './AiPanel.css';

/**
 * AiPanel — 우측 AI 서랍 (설계 판 A · 정의서 P1~P10).
 *
 * **AppLayout 이 전역으로 하나만 마운트한다.** 진입점(대시보드의 떠 있는 단추 · ⑤ 레일의 AI 칸)은
 * `aiPanelStore` 를 토글할 뿐이고 패널 본체는 여기 하나다 — 메모 패널과 같은 계약이다.
 *
 * ── 무엇을 메모 패널에서 그대로 가져왔나 ──────────────────────────────────
 * 우측 서랍은 이 저장소에 **이미 완성된 선례**가 있다(`MemoPanel`). 폭 전이로 열고 닫는 방식 ·
 * `right: var(--app-rightrail-w)` 로 ⑤ 레일 **옆에** 붙는 앵커 · `PaneResizer` 폭 조절 ·
 * 인라인 도킹 시 `body` 클래스로 본문을 미는 방법까지 같다. 새로 짜지 않고 본떴다.
 *
 * ── 무엇이 다른가 ─────────────────────────────────────────────────────────
 * ★★★ **상단은 아이콘 하나다**(P4·P5). 단추 넷을 늘어놓지 않고, 누르면 **아래로** 목록이 펼쳐져
 *    도킹 둘(체크가 붙는 **상태**) + 구분선 + 「전체」(체크 없는 **행동**)가 그 안에 있다.
 * ★★★ **「전체」는 도킹이 아니라 이동이다**(P7 · 2026-09-09 사용자 결정). 패널을 크게 펼치는 게
 *    아니라 **닫고 AI 메뉴로 나간다** — 그래서 체크가 안 붙고 상태 축도 안 는다.
 * ★★ 폭은 400/360/720 으로 메모(360/300/640)보다 한 단 넓다 — 도구 결과 표가 들어간다(P1).
 * ★★ 겹침은 `--z-drawer`(500)다 — 토큰에 「메모 패널 · 서랍」으로 이미 있다(P3).
 *    ⚠ 메모 패널은 `--z-dropdown`(100)을 쓰고 있는데, 그것을 따라가지 않는다.
 *
 * ── 판 A 의 몸통은 «비어 있다» ────────────────────────────────────────────
 * ★★★ 대화 배선은 판 B(`/api/v1/assist/turn`)의 것이다. 그래서 컴포저를 **그리지 않는다** —
 *    눌러도 아무 일이 없는 입력창·토글·모델 고르기를 세우면 그게 거짓말이다.
 *    대신 **지금 실제로 AI 에게 물을 수 있는 곳**(채팅 `/ai `)으로 가는 길을 적는다.
 */

/** 도킹 목록 — 값과 이름을 한 곳에 둔다. */
const DOCKS: ReadonlyArray<{ value: AiDock; label: string; icon: typeof Columns }> = [
  { value: 'inline', label: '사이드바', icon: Columns },
  { value: 'overlay', label: '플로팅', icon: Square },
];

export function AiPanel() {
  const navigate = useNavigate();

  const open = useAiPanelStore((s) => s.open);
  const dock = useAiPanelStore((s) => s.dock);
  const width = useAiPanelStore((s) => s.width);
  const closePanel = useAiPanelStore((s) => s.closePanel);
  const setDock = useAiPanelStore((s) => s.setDock);
  const setWidth = useAiPanelStore((s) => s.setWidth);

  const { shouldRender, isLeaving } = useModalLeave(open, 200);
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * 열림 슬라이드 — 마운트하는 프레임에 곧바로 열림 클래스를 주면 **시작 상태가 없어** 전이가
   * 생략되고 "툭" 하고 나타난다(메모 패널이 실제로 겪은 것). 닫힘(폭 0)으로 한 번 레이아웃을
   * 확정한 뒤 열림 클래스를 붙여 닫힐 때와 **같은** 폭 전이로 들어오게 한다.
   */
  const panelRef = useRef<HTMLElement | null>(null);
  const [entered, setEntered] = useState(false);
  useLayoutEffect(() => {
    if (!shouldRender || isLeaving) { setEntered(false); return; }
    void panelRef.current?.getBoundingClientRect().width;
    setEntered(true);
  }, [shouldRender, isLeaving]);

  /** 인라인 도킹이면 본문을 민다 — 메모 패널과 같은 방식(기존 레이아웃 DOM 무수정). */
  const inlineDocked = shouldRender && !isLeaving && dock === 'inline';
  useEffect(() => {
    document.body.classList.toggle('ai-inline-docked', inlineDocked);
    return () => { document.body.classList.remove('ai-inline-docked'); };
  }, [inlineDocked]);

  /** 폭은 CSS 변수로 흘린다 — 인라인 도킹의 본문 여백도 같은 값을 쓴다. */
  useEffect(() => {
    document.documentElement.style.setProperty('--ai-panel-w', `${width}px`);
  }, [width]);

  // Esc — 메뉴가 열려 있으면 메뉴만 닫고, 아니면 패널을 닫는다(안쪽부터 벗긴다).
  useEffect(() => {
    if (!shouldRender) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (menuOpen) { setMenuOpen(false); return; }
      closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [shouldRender, menuOpen, closePanel]);

  // 바깥을 누르면 펼침 목록만 닫는다.
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('mousedown', onDown); };
  }, [menuOpen]);

  /**
   * ★★★ **AI 메뉴 안에서는 패널이 뜨지 않는다** (2026-09-11 사용자 지시).
   *
   * 이 패널은 `AppLayout` 이 **모든 화면에** 그린다. 그래서 「전체」로 넘어가 AI 메뉴에 와도
   * 패널이 그대로 떠 있어, **같은 대화가 두 곳에 겹쳐** 보였다. 전체 화면이 곧 그 대화다.
   *
   * ⭑ 「전체」 단추(`goFull`)는 제 손으로 닫지만, **좌측 레일로 AI 메뉴에 들어오는 길**도 있다 —
   *   그쪽은 아무도 안 닫는다. 그래서 **단추가 아니라 «자리»에 규칙을 건다**: AI 구역에 있으면
   *   열려 있던 것도 닫는다.
   * ⭑ 대화는 안 지운다 — 같은 `aiChatStore` 를 전체 화면이 이어 받는다(정의서 M6).
   */
  const { pathname } = useLocation();
  const inAiSection = pathname === AI_HOME_PATH || pathname.startsWith(`${AI_HOME_PATH}/`);
  useEffect(() => {
    if (inAiSection && open) closePanel();
  }, [inAiSection, open, closePanel]);

  const goFull = useCallback(() => {
    setMenuOpen(false);
    closePanel();
    navigate(AI_HOME_PATH);
  }, [closePanel, navigate]);

  /* ★ 닫히는 것을 기다리지 않고 **그리지도 않는다** — 안 그러면 넘어가는 순간 한 번 깜빡인다. */
  if (inAiSection) return null;
  if (!shouldRender) return null;

  const isOpen = entered && !isLeaving;

  return (
    <aside
      ref={panelRef}
      className={[
        'ai-panel',
        isOpen ? 'is-open' : '',
        dock === 'inline' ? 'ai-panel--inline' : '',
      ].filter(Boolean).join(' ')}
      aria-label="AI"
      aria-hidden={!isOpen || undefined}
    >
      <PaneResizer
        anchor="right"
        width={width}
        min={AI_PANEL_MIN_WIDTH}
        max={AI_PANEL_MAX_WIDTH}
        defaultWidth={AI_PANEL_DEFAULT_WIDTH}
        label="AI 패널 폭 조절"
        onResize={setWidth}
        baseClass="ai-resizer"
      />

      <div className="ai-panel__top">
        <div className="ai-panel__menu" ref={menuRef}>
          <button
            type="button"
            className="ai-panel__menubtn"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((v) => !v)}
            title="패널 메뉴"
          >
            <Columns size={16} aria-hidden />
            <ChevronDown size={12} aria-hidden />
          </button>
          {menuOpen && (
            <div className="ai-panel__drop" role="menu" aria-label="패널 메뉴">
              {DOCKS.map((d) => {
                const Icon = d.icon;
                const on = dock === d.value;
                return (
                  <button
                    key={d.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={on}
                    className={`ai-panel__dropitem${on ? ' is-on' : ''}`}
                    onClick={() => { setDock(d.value); setMenuOpen(false); }}
                  >
                    <Icon size={14} aria-hidden />
                    {d.label}
                    {/* ★ 체크는 «상태»에만 붙는다 — 아래 「전체」는 행동이라 안 붙는다(P7). */}
                    {on && <Check size={14} className="ai-panel__ck" aria-hidden />}
                  </button>
                );
              })}
              <button
                type="button"
                role="menuitem"
                className="ai-panel__dropitem ai-panel__dropgo"
                onClick={goFull}
              >
                <Maximize2 size={14} aria-hidden />
                <span className="ai-panel__gotext">
                  전체
                  <span className="ai-panel__gosub">AI 메뉴로 이동</span>
                </span>
                <ExternalLink size={14} className="ai-panel__ck" aria-hidden />
              </button>
            </div>
          )}
        </div>

        <span className="ai-panel__title">AI</span>

        <button type="button" className="ai-panel__x" onClick={closePanel} title="닫기" aria-label="AI 패널 닫기">
          <X size={16} aria-hidden />
        </button>
      </div>

      {/* 대화·컴포저는 AI 메뉴와 **같은 부품**이다(정의서 M6) — 폭만 다르다.
          대화 내용은 `aiChatStore` 가 들고 있어 패널을 닫아도, 전체로 넘어가도 이어진다. */}
      <div className="ai-panel__body">
        <AiConversation
          emptyHint={<p>캘린더 · 근태 · 회의록은 지금 바로 물어볼 수 있습니다.</p>}
        />
      </div>
      <AiComposer />
    </aside>
  );
}
